import { useEffect, useState, type RefObject } from 'react'
import { IDLE_MS } from '../../slides/collab'

/**
 * True once the member has gone IDLE_MS (2 min) without input inside `ref`'s element; the next
 * input (a press, a key, the wheel) flips it back. An idle member keeps its presence but stops
 * holding a human lock (lock conflicts, AB#296). Shared by the prose and grid editors — the same
 * rule the slides editor applies to its selection.
 *
 * A hidden tab does NOT count as idle (AB#292): the browser reports 'hidden' on a mere alt-tab or
 * a covered window, so visibility plays no part. `enabled: false` (a viewer, no awareness) keeps
 * it permanently not-idle and arms no timer.
 */
export function useIdle(ref: RefObject<HTMLElement | null>, enabled: boolean): boolean {
  const [idle, setIdle] = useState(false)
  useEffect(() => {
    if (!enabled) {
      setIdle(false)
      return
    }
    let timer = setTimeout(() => setIdle(true), IDLE_MS)
    const onInput = (e: Event): void => {
      if (!ref.current?.contains(e.target as Node)) return
      clearTimeout(timer)
      timer = setTimeout(() => setIdle(true), IDLE_MS)
      setIdle(false)
    }
    const inputs = ['pointerdown', 'mousedown', 'keydown', 'wheel'] as const
    for (const type of inputs) document.addEventListener(type, onInput, true)
    return () => {
      clearTimeout(timer)
      for (const type of inputs) document.removeEventListener(type, onInput, true)
    }
  }, [ref, enabled])
  return idle
}
